import { computed, makeObservable, observable, runInAction } from 'mobx';
import { AgenturStore } from '../agentur.store';
import { AgenturModel } from 'app/models/agentur.model';
import _ from 'lodash';
import { BaseUiStore } from './base.ui.store';
import { AgtAmisNowStore, AgenturAmisNowModel } from '../agt.amisnow.store';

export class AmisNowModel {
	m: AgenturAmisNowModel;

	telefonieNachrichVonKvmHalle: number;
	aufgabeAusAkuma: number;
	kundennachrichtVertriebschance: number;
	kuendigungsinformation: number;
	aufgabeAusAmisnowPostkorb: number;
	freigabeAngebot: number;
	kundennachrichtServiceanliegen: number;
	meineAllianzNachricht: number;
	telefonieNachrichtVonKvmHalleInteressent: number;
	kundennachrichtVertriebschanceWiederanlage: number;
	summe: number;
	davonRead: number;
	quoteRead: number;
	eigeneAufgaben: number;

	bm: number;

	constructor(opts: AgenturAmisNowModel) {
		this.m = opts;
		const j = opts.j;

		this.bm = this.m.bm;

		// Map from short server keys to descriptive client properties
		this.telefonieNachrichVonKvmHalle = j?.a || 0;
		this.aufgabeAusAkuma = j?.b || 0;
		this.kundennachrichtVertriebschance = j?.c || 0;
		this.kuendigungsinformation = j?.d || 0;
		this.aufgabeAusAmisnowPostkorb = j?.f || 0;
		this.freigabeAngebot = j?.g || 0;
		this.kundennachrichtServiceanliegen = j?.h || 0;
		this.meineAllianzNachricht = j?.i || 0;
		this.telefonieNachrichtVonKvmHalleInteressent = j?.j || 0;
		this.eigeneAufgaben = j?.k || 0;
		this.kundennachrichtVertriebschanceWiederanlage = j?.l || 0;
		this.summe = j?.m || 0;
		this.davonRead = j?.n || 0;
		this.quoteRead = j?.o || 0;
	}
}

export class AmisNowVm {
	agt: AgenturModel;
	@observable
	items: AmisNowModel[] = [];

	constructor(agt: AgenturModel, models: AgenturAmisNowModel[]) {
		makeObservable(this);
		this.agt = agt;
		this.items = models.map((model) => new AmisNowModel(model));
	}

	// Aggregate all months data
	@computed
	get aggregated(): AmisNowModel | undefined {
		if (this.items.length === 0) return undefined;

		// Create a fake KvAgtMsLiteModel with aggregated data using single-character keys
		const aggregatedData = {
			a: 0,
			b: 0,
			c: 0,
			d: 0,
			e: 0,
			f: 0,
			g: 0,
			h: 0,
			i: 0,
			j: 0,
			k: 0,
			l: 0,
			m: 0,
			n: 0,
			o: 0,
		};

		this.items.forEach((item) => {
			aggregatedData.a += item.telefonieNachrichVonKvmHalle || 0;
			aggregatedData.b += item.aufgabeAusAkuma || 0;
			aggregatedData.c += item.kundennachrichtVertriebschance || 0;
			aggregatedData.d += item.kuendigungsinformation || 0;
			aggregatedData.f += item.aufgabeAusAmisnowPostkorb || 0;
			aggregatedData.g += item.freigabeAngebot || 0;
			aggregatedData.h += item.kundennachrichtServiceanliegen || 0;
			aggregatedData.i += item.meineAllianzNachricht || 0;
			aggregatedData.j += item.telefonieNachrichtVonKvmHalleInteressent || 0;
			aggregatedData.k += item.eigeneAufgaben || 0;
			aggregatedData.l += item.kundennachrichtVertriebschanceWiederanlage || 0;
			aggregatedData.m += item.summe || 0;
			aggregatedData.n += item.davonRead || 0;
		});

		// Calculate quote
		aggregatedData.o = aggregatedData.m > 0 ? aggregatedData.n / aggregatedData.m : 0;

		const fakeModel: AgenturAmisNowModel = {
			agtId: this.agt.agtId,
			vtnrId: this.agt.agtId,
			bm: 0, // Not relevant for aggregated
			j: aggregatedData,
		} as any;

		return new AmisNowModel(fakeModel);
	}
}

export class AgtAmisNowUiStore extends BaseUiStore<AmisNowVm> {
	name = 'AgtAmisNowUiStore';
	agenturAmisNowStore: AgtAmisNowStore;
	agenturStore: AgenturStore;

	constructor(agenturStore: AgenturStore, agenturAmisNowStore: AgtAmisNowStore) {
		super();
		makeObservable(this);
		this.agenturAmisNowStore = agenturAmisNowStore;
		this.agenturStore = agenturStore;
	}

	@observable
	minBmCurrent?: number;

	@observable
	maxBmCurrent?: number;

	@computed
	get currentYear(): number {
		return new Date().getFullYear();
	}

	@computed
	get minBm(): number {
		if (this.minBmCurrent) {
			return this.minBmCurrent;
		}
		return this.currentYear * 100 + 1; // Start of current year
	}

	@computed
	get maxBm(): number {
		if (this.maxBmCurrent) {
			return this.maxBmCurrent;
		}
		// Get current month
		const now = new Date();
		return this.currentYear * 100 + now.getMonth() + 1;
	}

	@computed
	get currentBmItems(): AmisNowVm[] {
		return this.items
			.map((vm) => {
				const filteredItems = vm.items.filter((item) => {
					if (this.minBm && item.bm >= this.minBm) {
						if (this.maxBm && item.bm <= this.maxBm) {
							return true;
						}
					}
					return false;
				});

				// Convert AmisNowModel[] back to AgenturAmisNowModel[]
				const models = filteredItems.map((item) => item.m);
				const newVm = new AmisNowVm(vm.agt, models);
				return newVm;
			})
			.filter((vm) => vm.items.length > 0);
	}

	@computed
	get bmOpts() {
		// Get all unique BMs from the data
		const bmsSet = new Set(this.items.flatMap((vm) => vm.items.map((item) => item.bm)));
		const allBms = Array.from(bmsSet).sort((a, b) => a - b);

		return allBms.map((bm) => ({
			value: bm,
			label: this.formatBm(bm),
		}));
	}

	private formatBm(bm: number): string {
		const year = Math.floor(bm / 100);
		const month = bm % 100;
		const monthNames = ['', 'Jan', 'Feb', 'Mär', 'Apr', 'Mai', 'Jun', 'Jul', 'Aug', 'Sep', 'Okt', 'Nov', 'Dez'];
		return `${monthNames[month]} ${year}`;
	}

	allVmItems: AmisNowVm[] = [];

	async _baseLoad() {
		if (this.items.length > 0) {
			return;
		}

		const bms = await this.agenturAmisNowStore.getDistinctBMs();
		const agts = await this.agenturStore.findAll();
		const res: AmisNowVm[] = [];

		for (const agt of agts) {
			const vm = new AmisNowVm(agt, [{}] as any[]);
			res.push(vm);
		}

		// Get all unique BMs from the data
		const allBms = bms.sort((a, b) => a - b);
		runInAction(() => {
			this.items = res;
			if (allBms.length > 0) {
				// Set date range to current year
				const currentYear = new Date().getFullYear();
				const yearStart = currentYear * 100 + 1;
				this.minBmCurrent = Math.max(yearStart, allBms[0]);
				this.maxBmCurrent = allBms[allBms.length - 1];
				this.datenStand = this.maxBmCurrent;
			}
			this.allVmItems = res;
		});
	}

	async aggreagateAgtsByBm(minBm?: number, maxBm?: number) {
		// Create a cache key based on the parameters
		const cacheKey = `aggreagateAgtsByKw_${minBm || 'null'}_${maxBm || 'null'}`;

		return this.withCache(
			cacheKey,
			async () => {
				await this.load();
				let models = await this.agenturAmisNowStore.aggregateByBm(minBm, maxBm);
				const res: AmisNowVm[] = this.allVmItems.map((vm) => {
					const p = models.filter((m) => m.agtId === vm.agt.agtId);
					return new AmisNowVm(vm.agt, p || ([] as any[]));
				});

				runInAction(() => {
					this.items = res;
				});
				return this.items;
			},
			{
				cacheResult: true,
				measureTime: true,
			},
		);
	}

	async loadAmisNowHistoryLast12Months(agtId: number): Promise<
		{
			bm: string;
			bmRaw: number;
			aufgabenGesamt: number;
			aufgabenAdz: number;
			adzAufgabenquote: number;
		}[]
	> {
		const cacheKey = `AgtAmisNowUiStore.loadAmisNowHistoryLast12Months${agtId}`;

		return this.withCache(
			cacheKey,
			async () => {
				// Ensure base data is loaded
				await this.load();

				// Get available BMs from the store
				const bms = await this.agenturAmisNowStore.getDistinctBMs();

				// Calculate current BM (YYYYMM format)
				const now = new Date();
				const currentBm = now.getFullYear() * 100 + (now.getMonth() + 1);

				// Filter out future months
				const pastBms = bms.filter(bm => bm <= currentBm);

				// Get last 12 months of available past data
				const last12Bms = pastBms.slice(-12);

				// Load data for each month
				const result = await Promise.all(
					last12Bms.map(async (bm) => {
						const models = await this.agenturAmisNowStore.aggregateByBm(bm, bm);
						const agtData = models.find((m) => m.agtId === agtId);

						// Calculate aufgabenAdz - sum of all amisNowProps where adzCategory === 'vertriebsChance'
						let aufgabenAdz = 0;
						if (agtData) {
							aufgabenAdz = (agtData.j?.c || 0) + // kundennachrichtVertriebschance
								(agtData.j?.d || 0) + // kuendigungsinformation
								(agtData.j?.g || 0) + // freigabeAngebot
								(agtData.j?.j || 0) + // telefonieNachrichtVonKvmHalleInteressent
								(agtData.j?.l || 0) + // kundennachrichtVertriebschanceWiederanlage
								(agtData.j?.b || 0);  // aufgabeAusAkuma
						}

						const aufgabenGesamt = agtData?.j?.m || 0;

						return {
							bm: this.formatBm(bm),
							bmRaw: bm,
							aufgabenGesamt,
							aufgabenAdz,
							adzAufgabenquote: aufgabenGesamt > 0 ? aufgabenAdz / aufgabenGesamt : 0,
						};
					}),
				);

				return result;
			},
			{
				cacheResult: true,
			},
		);
	}

	async loadAmisNowHistoryLast12MonthsAggregated(agtIds: number[]): Promise<
		{
			bm: string;
			bmRaw: number;
			aufgabenGesamt: number;
			aufgabenAdz: number;
			adzAufgabenquote: number;
		}[]
	> {
		const cacheKey = `AgtAmisNowUiStore.loadAmisNowHistoryLast12MonthsAggregated_${agtIds.sort().join('_')}`;

		return this.withCache(
			cacheKey,
			async () => {
				// Ensure base data is loaded
				await this.load();

				// Get available BMs from the store
				const bms = await this.agenturAmisNowStore.getDistinctBMs();

				// Calculate current BM (YYYYMM format)
				const now = new Date();
				const currentBm = now.getFullYear() * 100 + (now.getMonth() + 1);

				// Filter out future months
				const pastBms = bms.filter(bm => bm <= currentBm);

				// Get last 12 months of available past data
				const last12Bms = pastBms.slice(-12);

				// Load data for each month and aggregate across agencies
				const result = await Promise.all(
					last12Bms.map(async (bm) => {
						const models = await this.agenturAmisNowStore.aggregateByBm(bm, bm);

						// Aggregate across specified agencies
						let totalAufgabenGesamt = 0;
						let totalAufgabenAdz = 0;

						agtIds.forEach((agtId) => {
							const agtData = models.find((m) => m.agtId === agtId);
							if (agtData) {
								totalAufgabenGesamt += agtData.j?.m || 0;
								// Calculate aufgabenAdz - sum of all amisNowProps where adzCategory === 'vertriebsChance'
								totalAufgabenAdz += (agtData.j?.c || 0) + // kundennachrichtVertriebschance
									(agtData.j?.d || 0) + // kuendigungsinformation
									(agtData.j?.g || 0) + // freigabeAngebot
									(agtData.j?.j || 0) + // telefonieNachrichtVonKvmHalleInteressent
									(agtData.j?.l || 0) + // kundennachrichtVertriebschanceWiederanlage
									(agtData.j?.b || 0);  // aufgabeAusAkuma
							}
						});

						return {
							bm: this.formatBm(bm),
							bmRaw: bm,
							aufgabenGesamt: totalAufgabenGesamt,
							aufgabenAdz: totalAufgabenAdz,
							adzAufgabenquote: totalAufgabenGesamt > 0 ? totalAufgabenAdz / totalAufgabenGesamt : 0,
						};
					}),
				);

				return result;
			},
			{
				cacheResult: true,
			},
		);
	}
}

export interface IAmisNowProps {
	sort: number;
	label: string;
	path: string;
	shortPath: string;
	group?: string;
	adzCategory?: string;
}
export const amisNowProps: IAmisNowProps[] = [
	{
		sort: 0,
		label: 'Kundennachricht Vertriebschance',
		path: `amisNow.aggregated.kundennachrichtVertriebschance`,
		shortPath: `kundennachrichtVertriebschance`,
		group: 'anfrage',
		adzCategory: 'vertriebsChance',
	},
	{
		sort: 1,
		label: 'Kündigungs\u00ADinformation', // soft hyphen
		path: `amisNow.aggregated.kuendigungsinformation`,
		shortPath: `kuendigungsinformation`,
		group: 'general',
		adzCategory: 'vertriebsChance',
	},
	{
		sort: 2,
		label: 'Freigabe Angebot',
		path: `amisNow.aggregated.freigabeAngebot`,
		shortPath: `freigabeAngebot`,
		group: 'anfrage',
		adzCategory: 'vertriebsChance',
	},
	{
		sort: 3,
		label: 'Telefonie Nachricht von KVM Halle - Interessent',
		path: `amisNow.aggregated.telefonieNachrichtVonKvmHalleInteressent`,
		shortPath: `telefonieNachrichtVonKvmHalleInteressent`,
		group: 'kvm',
		adzCategory: 'vertriebsChance',
	},
	{
		sort: 4,
		label: 'Kundennachricht Vertriebschance Wiederanlage',
		path: `amisNow.aggregated.kundennachrichtVertriebschanceWiederanlage`,
		shortPath: `kundennachrichtVertriebschanceWiederanlage`,
		group: 'anfrage',
		adzCategory: 'vertriebsChance',
	},
	{
		sort: 5,
		label: 'Aufgabe aus AKUMA',
		path: `amisNow.aggregated.aufgabeAusAkuma`,
		shortPath: `aufgabeAusAkuma`,
		group: 'internal',
		adzCategory: 'vertriebsChance',
	},
	// ================================
	{
		sort: 6,
		label: 'Aufgabe aus AMIS.NOW Postkorb',
		path: `amisNow.aggregated.aufgabeAusAmisnowPostkorb`,
		shortPath: `aufgabeAusAmisnowPostkorb`,
		group: 'internal',
		adzCategory: 'eigeneAufgaben',
	},
	{
		sort: 7,
		label: 'Eigene Aufgaben',
		path: `amisNow.aggregated.eigeneAufgaben`,
		shortPath: `eigeneAufgaben`,
		group: 'internal',
		adzCategory: 'eigeneAufgaben',
	},
	// ================================
	{
		sort: 8,
		label: 'Telefonie Nachricht von KVM Halle - Kunde',
		path: `amisNow.aggregated.telefonieNachrichVonKvmHalle`,
		shortPath: `telefonieNachrichVonKvmHalle`,
		group: 'kvm',
		adzCategory: 'sontiges',
	},
	{
		sort: 9,
		label: 'Kundennachricht Serviceanliegen',
		path: `amisNow.aggregated.kundennachrichtServiceanliegen`,
		shortPath: `kundennachrichtServiceanliegen`,
		group: 'general',
		adzCategory: 'sontiges',
	},
	{
		sort: 10,
		label: 'Meine Allianz Nachricht',
		path: `amisNow.aggregated.meineAllianzNachricht`,
		shortPath: `meineAllianzNachricht`,
		group: 'general',
		adzCategory: 'sontiges',
	},
	// ================================
	{
		sort: 11,
		label: 'Summe',
		path: `amisNow.aggregated.summe`,
		shortPath: `summe`,
	},
	{
		sort: 12,
		label: 'Davon Gelesen',
		path: `amisNow.aggregated.davonRead`,
		shortPath: `davonRead`,
	},
	// {
	// 	sort: 14,
	// 	label: 'Quote Gelesen',
	// 	path: `amisNow.aggregated.quoteRead`,
	// 	shortPath: `quoteRead`,
	// },
];
